import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

interface PaginationProps {
  /** Zero-indexed current page. */
  page: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  onPageChange: (next: number) => void;
  /** Compact mode shows only the prev/next pair without the range summary. */
  compact?: boolean;
  className?: string;
}

/**
 * Shared pagination strip. Renders below every paginated list (Requests,
 * Customers, Payments, Messages) so the visual rhythm stays consistent.
 *
 * Always shown when there are *any* rows so staff can see total count even
 * when only a single page exists.
 */
export function Pagination({
  page,
  totalPages,
  totalElements,
  pageSize,
  onPageChange,
  compact = false,
  className,
}: PaginationProps) {
  if (totalElements === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalElements);

  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-5 py-3 border-t border-line bg-white',
        className,
      )}
    >
      {!compact && (
        <div className="text-caption text-ink-muted tabular">
          Showing{' '}
          <span className="font-semibold text-ink">
            {from.toLocaleString()}
          </span>{' '}
          –{' '}
          <span className="font-semibold text-ink">
            {to.toLocaleString()}
          </span>{' '}
          of{' '}
          <span className="font-semibold text-ink">
            {totalElements.toLocaleString()}
          </span>
        </div>
      )}
      <div className={cn('flex items-center gap-1', compact && 'ml-auto')}>
        <PageButton
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </PageButton>

        {/* Page numbers — show up to 5 with the current page centred. */}
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === '…' ? (
            <span
              key={`gap-${i}`}
              className="px-2 text-caption text-ink-hint select-none"
            >
              …
            </span>
          ) : (
            <PageButton
              key={p}
              active={p === page}
              onClick={() => onPageChange(p)}
            >
              {(p + 1).toString()}
            </PageButton>
          ),
        )}

        <PageButton
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  children,
  onClick,
  disabled = false,
  active = false,
  ...rest
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'min-w-[34px] h-8 px-2 inline-flex items-center justify-center',
        'text-caption font-semibold rounded-md transition-colors',
        active
          ? 'bg-status-info-bg text-brand-blue'
          : 'text-ink hover:bg-canvas',
        disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * Builds a windowed page list:
 *   1 2 3 4 5
 *   1 2 3 ... 7 8 9
 *   1 ... 5 6 7 ... 9
 * Returns numeric page indices (zero-based) interleaved with '…' separators.
 */
function buildPageWindow(page: number, total: number): (number | '…')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const window: (number | '…')[] = [];
  window.push(0);
  if (page > 2) window.push('…');
  const start = Math.max(1, page - 1);
  const end = Math.min(total - 2, page + 1);
  for (let p = start; p <= end; p++) window.push(p);
  if (page < total - 3) window.push('…');
  window.push(total - 1);
  return window;
}
